import { SegmentedOption } from '@ca/ui/elements/Segmented/types'
import { Phase } from './types'
import type { HsaDebug, SHCaptureData } from '../index'

export const ROTATION_X_THRESHOLD = 5
export const ROTATION_Y_THRESHOLD = 5
export const EYE_OPEN_THRESHOLD = 0.8
export const BUFFER_SIZE = 15
export const VARIANCE_THRESHOLD = 0.6
export const STABLE_FOR_MS = 1000
export const SAMPLE_DURATION_MS = 3000
export type TabId = 'live' | 'pd' | 'sh' | 'hsa'

export const TABS: SegmentedOption<TabId>[] = [
  { id: 'pd', label: 'PD' },
  { id: 'sh', label: 'SH' },
  { id: 'hsa', label: 'HSA' },
  { id: 'live', label: 'Live' },
]

export const PHASE_COLOR: Record<Phase, string> = {
  idle: 'bg-gray-300',
  calibrating: 'bg-blue-400',
  detecting: 'bg-orange-400',
  findingFrame: 'bg-yellow-400',
  tooClose: 'bg-orange-400',
  tooFar: 'bg-orange-400',
  positioning: 'bg-yellow-400',
  opening: 'bg-yellow-400',
  stabilising: 'bg-yellow-400',
  sampling: 'bg-green-500',
  complete: 'bg-blue-500',
  failed: 'bg-red-500',
}

export const PHASE_PROMPT: Record<Phase, string> = {
  idle: '',
  calibrating: 'Setting Up…',
  detecting: 'Look at the camera',
  findingFrame: 'Hold still',
  tooClose: 'Move back slightly',
  tooFar: 'Move closer slightly',
  positioning: 'Face the camera straight on',
  opening: 'Keep eyes open',
  stabilising: 'Keep still…',
  sampling: 'Keep holding still',
  complete: 'Measurement complete',
  failed: 'Capture failed',
}

export const HSA_PHASE_PROMPT: Record<Phase, string> = {
  ...PHASE_PROMPT,
  positioning: 'Turn to the side',
  findingFrame: 'Keep glasses visible',
  sampling: 'Hold still',
}

export const PHASE_DETAIL: Partial<Record<Phase, string>> = {
  detecting: 'Position your face in view',
  findingFrame: 'Keep the frame rim visible and avoid glare',
  tooClose: 'A little more distance helps the camera read the frame',
  tooFar: 'A little closer helps the camera read the frame',
  positioning: 'Small head turns are OK; keep looking toward the screen',
  sampling: 'We are capturing good frames now',
  failed: 'Clear and try again',
}

export const HSA_PHASE_DETAIL: Partial<Record<Phase, string>> = {
  ...PHASE_DETAIL,
  positioning: 'Either side profile is OK',
  findingFrame: 'Keep the lens and frame edge visible',
  sampling: 'We are capturing good side-profile frames now',
}

export const HSA_REJECTION_PROMPT: Record<string, string> = {
  'unstable-tracking': 'Hold still',
  'turn-to-side': 'Turn to the side',
  'turn-slightly-toward-camera': 'Turn slightly toward camera',
  'level-head': 'Level your head',
  'lens-detector-not-implemented': 'Keep glasses visible',
}

export const SH_REJECTION_PROMPT: Record<string, string> = {
  'glasses-not-detected': 'Make Sure You Are Wearing Glasses',
  'glasses-presence-unavailable': 'Keep Glasses Visible',
  'glasses-detector-unavailable': 'Keep Glasses Visible',
}

export const SH_REJECTION_DETAIL: Record<string, string> = {
  'glasses-not-detected': 'The camera needs to see your glasses clearly',
  'glasses-presence-unavailable': 'Keep the frame rim visible and avoid glare',
  'glasses-detector-unavailable': 'Keep the frame rim visible and avoid glare',
}

export const shPromptForCapture = (
  phase: Phase,
  capture?: SHCaptureData | null
) => {
  const reason = capture?.rejectionReason
  if (reason) {
    return SH_REJECTION_PROMPT[reason] ?? PHASE_PROMPT[phase]
  }
  return PHASE_PROMPT[phase]
}

export const shDetailForCapture = (
  phase: Phase,
  capture?: SHCaptureData | null
) => {
  const reason = capture?.rejectionReason
  if (reason) {
    return SH_REJECTION_DETAIL[reason] ?? PHASE_DETAIL[phase]
  }
  return PHASE_DETAIL[phase]
}

export const hsaPromptForDebug = (
  phase: Phase,
  debug?: HsaDebug | null
) => {
  if (debug?.rejectionReason) {
    return HSA_REJECTION_PROMPT[debug.rejectionReason] ?? HSA_PHASE_PROMPT[phase]
  }
  return HSA_PHASE_PROMPT[phase]
}
